import { memo, useCallback } from "react";

import InputTextArea from "@/components/Input/InputTextArea";

import type { FieldRenderInputProps } from "../../types";

import { BaseField } from "./BaseField";
import type { BaseFieldProps } from "./BaseField";
interface TextAreaFieldProps extends BaseFieldProps {
  /** 最大入力文字数  */
  maxLength?: number;

  /** 最小入力文字数 */
  minLength?: number;

  /** 行数 */
  rows?: number;

  /** プレースホルダー */
  placeholder?: string;
}

/**
 * テキストエリア入力フィールドコンポーネント
 * 複数行のテキスト入力が可能なエリアを提供
 */
export const TextAreaField = memo(
  ({
    fieldId,
    field,
    layout,
    readOnly,
    disabled,
    className,
    maxLength,
    minLength,
    rows = 4,
    placeholder,
    "data-testid": testId,
  }: TextAreaFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        // 入力値を文字列に変換
        const stringValue =
          props.value !== undefined ? String(props.value) : "";

        // 入力変更ハンドラー
        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          props.onChange(e.target.value);
        };

        return (
          <InputTextArea
            id={props.id}
            name={props.name}
            value={stringValue}
            onChange={handleChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            placeholder={props.placeholder || placeholder}
            disabled={props.disabled}
            readOnly={props.readOnly}
            required={props.required}
            maxLength={maxLength}
            minLength={minLength}
            rows={rows}
            error={!!props.error}
            aria-invalid={!!props.error}
            aria-describedby={props.error ? `${props.id}-error` : undefined}
            width="100%"
            className="w-full"
            data-testid={props["data-testid"] || "textarea-field"}
          />
        );
      },
      [maxLength, minLength, rows, placeholder],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        layout={layout}
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

TextAreaField.displayName = "TextAreaField";
